import { ActionTree, MutationTree } from "vuex";
import axios, { AxiosResponse } from "axios";
import { LossTypes } from "@/models/tpp-claims/LossTypes";
import { RootState } from "@/models/store/RootState";
import { TPPClaims } from "@/models/tpp-claims/TPPClaims";
import { TPPClaimsState } from "@/models/store/TPPClaimsState";
import { additionalInfoActions } from "@/store/tpp-claims-helpers/actions/additional-info-actions";
import { additionalInfoMutations } from "@/store/tpp-claims-helpers/mutations/additional-info-mutations";
import { filterParamsActions } from "@/store/tpp-claims-helpers/actions/filter-params-actions";
import { filterParamsMutations } from "@/store/tpp-claims-helpers/mutations/filter-params-mutations";
import { generateFilterQuery } from "@/helpers/tpp-claims/filter-params";
import { incidentInfoActions } from "@/store/tpp-claims-helpers/actions/incident-info-actions";
import { incidentInfoMutations } from "@/store/tpp-claims-helpers/mutations/incident-info-mutations";
import { propertyDamageActions } from "@/store/tpp-claims-helpers/actions/property-damage-actions";
import { propertyDamageMutations } from "@/store/tpp-claims-helpers/mutations/property-damage-mutations";
import { tenantInfoActions } from "@/store/tpp-claims-helpers/actions/tenant-info-actions";
import { tenantInfoMutations } from "@/store/tpp-claims-helpers/mutations/tenant-info-mutations";

export const state = {
  canEditClaim: false,
  claimStatusesList: [],
  claimStatusUpdate: {
    status: ""
  },
  filterParams: {
    claim_number: "",
    date: "",
    location_code: [],
    loss_type: [],
    name: "",
    status: []
  },
  loadingState: [],
  lossTypesList: [],
  selectedTPPClaim: {},
  tppClaimsList: []
};

export const actions: ActionTree<TPPClaimsState, RootState> = {
  getClaimStatuses: ({ commit, dispatch }) => {
    commit("addToLoadingState", "retrieving claim statuses");
    return axios
      .get(`tpp/claim_statuses`)
      .then((resp: AxiosResponse) => {
        commit("setClaimStatuses", resp.data);
        commit("removeFromLoadingState", "retrieving claim statuses");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", "Unable to retrieve claim statuses", { root: true });
        commit("removeFromLoadingState", "retrieving claim statuses");
        return Promise.reject(err);
      });
  },
  getLossTypes: ({ commit, dispatch }) => {
    commit("addToLoadingState", "retrieving loss types");
    return axios
      .get(`tpp/loss-types`)
      .then((resp) => {
        commit("setLossTypes", resp.data);
        commit("removeFromLoadingState", "retrieving loss types");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", "Unable to retrieve loss types", { root: true });
        commit("removeFromLoadingState", "retrieving loss types");
        return Promise.reject(err);
      });
  },
  getTPPClaims: ({ commit, dispatch, state }, payload?: string) => {
    const filters = generateFilterQuery(state.filterParams);
    let queryParam;
    payload ? queryParam = payload : queryParam = filters;
    commit("addToLoadingState", "getting tpp claims");
    return axios.get(`/tpp/claims?${queryParam}`)
      .then((resp: AxiosResponse) => {
        commit("setTPPClaims", resp.data);
        commit("removeFromLoadingState", "getting tpp claims");
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "getting tpp claims");
        throw new Error(err);
      });
  },
  updateTPPClaimStatus: ({ commit, dispatch, state }) => {
    commit("addToLoadingState", "updating tpp claim status");
    return axios.patch(`/tpp/claims/${state.selectedTPPClaim!.claim_number}/status`, state.claimStatusUpdate)
      .then((resp) => {
        dispatch("getTPPClaims", `filters[claim_number]=${state.selectedTPPClaim!.claim_number}`);
        commit("setCanEditClaimStatus", false);
        commit("setNewClaimStatus", "");
        commit("setSelectedTPPClaim", {});
        dispatch("snackbar/successMessage", `Successfully updated TPP Claim Status`, { root: true });
        commit("removeFromLoadingState", "updating tpp claim status");
        return resp;
      })
      .catch((err) => {
        dispatch("snackbar/errorMessage", err, { root: true });
        commit("removeFromLoadingState", "updating tpp claim status");
        throw new Error(err);
      });
  },
  handleSelectedTPPClaim: async({ commit }, payload: TPPClaims) => {
    commit("addToLoadingState", "setting selected tpp claim");
    await commit("setSelectedTPPClaim", payload);
    commit("removeFromLoadingState", "setting selected tpp claim");
  },
  handleCanEditClaimStatus: ({ commit }, payload: boolean) => {
    commit("setCanEditClaimStatus", payload);
  },
  handleNewClaimStatus: ({ commit }, payload: string) => {
    commit("setNewClaimStatus", payload);
  },
  ...filterParamsActions,
  ...additionalInfoActions,
  ...incidentInfoActions,
  ...propertyDamageActions,
  ...tenantInfoActions
};

export const mutations: MutationTree<TPPClaimsState> = {
  addToLoadingState: (state: TPPClaimsState, message: string) => {
    state.loadingState.push(message);
  },
  removeFromLoadingState: (state: TPPClaimsState, message: string) => {
    state.loadingState = state.loadingState.filter(loadingMessage => loadingMessage !== message);
  },
  setClaimStatuses: (state: TPPClaimsState, claimStatuses: []) => {
    state.claimStatusesList = claimStatuses;
  },
  setTPPClaims: (state: TPPClaimsState, tppClaims) => {
    state.tppClaimsList = tppClaims;
  },
  setLossTypes: (state: TPPClaimsState, lossTypes: LossTypes[]) => {
    state.lossTypesList = lossTypes;
  },
  setSelectedTPPClaim: (state: TPPClaimsState, tppClaim: TPPClaims) => {
    state.selectedTPPClaim = tppClaim;
  },
  setCanEditClaimStatus: (state: TPPClaimsState, editStatus: boolean) => {
    state.canEditClaim = editStatus;
  },
  setNewClaimStatus: (state: TPPClaimsState, claimStatus: string) => {
    state.claimStatusUpdate.status = claimStatus;
  },
  ...filterParamsMutations,
  ...additionalInfoMutations,
  ...incidentInfoMutations,
  ...propertyDamageMutations,
  ...tenantInfoMutations
};

export default {
  name: "tppClaims",
  namespaced: true,
  state,
  actions,
  mutations
};
